import React from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import * as R from "ramda";

import Icon from "components/Icon";
import Loader from "components/Loader";
import Text from "components/Text";
import ErrorMessage from "components/project_form/ErrorMessage";
import Label from "components/project_form/Label";
import InspectableComponent from "components/projects/InspectableComponent";
import { useAnswerContext } from "contexts/answerContext";
import { useCurrentTenant } from "contexts/currentTenant";
import useFocusedState from "hooks/useFocusedState";
import { useEntity } from "queries/entities";
import { useFieldWithChildren } from "queries/fields";
import { getAnswerForField, getErrorForField } from "reducers/answerContexts";
import { getType, isCalculated, isList } from "reducers/fields";
import { isChangeset } from "selectors/changesets";
import { formatId } from "utils/format";
import { isBlank, isString } from "utils/func";
import { childrenPropType, fieldPropType } from "utils/sharedPropTypes";

import styles from "./Field.scss";
import fieldForInputType from "./fieldLookup";
import { dataAttrsForField } from "./helpers";

const DefaultLayout = ({
  field,
  onFocus,
  onBlur,
  children,
  horizontalLabel,
  behaviorType,
  className,
  id,
  isNested = false,
}) => {
  const fieldTypeStyleKey = R.pipe(
    R.filter(Boolean),
    R.join("-"),
  )([getType(field), behaviorType, isList(field)]);

  const dataAttrs = isNested ? {} : dataAttrsForField(field);
  return (
    <div
      className={classnames(className, styles.fieldContainer, styles[fieldTypeStyleKey], {
        [styles.horizontal]: horizontalLabel,
      })}
      {...dataAttrs}
      onFocus={onFocus}
      onBlur={onBlur}
      id={id}
    >
      {children}
    </div>
  );
};

DefaultLayout.propTypes = {
  field: fieldPropType,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  children: childrenPropType.isRequired,
  horizontalLabel: PropTypes.bool,
  behaviorType: PropTypes.string,
  isNested: PropTypes.bool,
};

export const LayoutWithLock = ({ isLocked, children, ...props }) => (
  <div className={styles.fieldContainerWithLock}>
    <div className={styles.field}>
      <DefaultLayout {...props}>{children}</DefaultLayout>
    </div>
    {isLocked && (
      <div className={styles.fieldAnnotation}>
        <Icon icon="lock" size="lg" />
      </div>
    )}
  </div>
);

const isUnanswered = (value) =>
  R.ifElse(R.is(Object), R.pipe(R.values, R.all(isUnanswered)), isBlank)(value);

const FormField = ({
  Layout = DefaultLayout,
  field,
  hideLabel,
  hideRequired,
  forceRequired,
  hideHelp,
  behaviorType,
  fieldComponent,
  hideIfUnanswered,
  horizontalLabel,
  hideError,
  children,
  className,
  isNested,
  changeType,
  skipSave,
  ...props
}) => {
  const [isFocused, onFocus, onBlur] = useFocusedState(false);
  const answerContext = useAnswerContext();
  const tenant = useCurrentTenant();

  if (!tenant || !field) {
    return <Loader component />;
  }

  const isLocked =
    answerContext?.record?.locked_field_ids?.includes(field.id) || answerContext.readOnly;
  if (behaviorType !== "issued" && (isCalculated(field) || isLocked)) {
    behaviorType = "disabled";
  }

  const uiType = field.ui?.form_input?.type || "text";
  const Field = fieldComponent || fieldForInputType(uiType, { behaviorType });

  if (!Field) {
    return null;
  }

  if (field?.calculated && behaviorType !== "disabled") {
    console.log("Calculated field rendered with behaviorType", behaviorType, field);
    return null;
  }

  const {
    record,
    context,
    onChange: onChangeAnswerContext,
    onSave: onSaveAnswerContext,
    setError: setAnswerContextError,
    clearError: clearAnswerContextError,
  } = answerContext;

  if (!record) {
    console.error("An AnswerContext is required to render a field", answerContext);
  }

  // [jb] Component field children still get their value passed in by the parent until we add key_path to fields
  const value = R.isNil(props.value) ? getAnswerForField(record, field) : props.value;

  const onChange = (value) => {
    onChangeAnswerContext(field, value);
    if (props.onChange) props.onChange({ field, value, answerContext });
  };

  const onSave = () => {
    if (!skipSave) {
      onSaveAnswerContext(field);
    }
    if (props.onSave) props.onSave({ field, answerContext });
  };

  const onClear = () => {
    onChange(null);
    setTimeout(onSave, 0);
  };

  const setError = (error) => setAnswerContextError(field, error);
  const clearError = () => clearAnswerContextError(field);

  const error = getErrorForField(record, field);
  const unanswered = isUnanswered(value);

  if (hideIfUnanswered && unanswered && uiType !== "instruction") {
    return null;
  }

  const required =
    !hideRequired &&
    (field.required ||
      forceRequired ||
      (!isChangeset(record) && R.propOr(false, "all_fields_required", context)));
  const labelProps = {
    field,
    required,
    hideHelp,
    horizontalLabel,
    behaviorType,
  };
  const labelHidden =
    hideLabel ||
    (behaviorType !== "issued" &&
      R.includes(uiType, ["instruction", "check_box", "terms_and_condition", "none"]));

  return (
    <InspectableComponent identifier={field.identifier}>
      <Layout
        field={field}
        onFocus={onFocus}
        onBlur={onBlur}
        horizontalLabel={horizontalLabel}
        behaviorType={behaviorType}
        className={classnames(className, {
          [styles.answerChangedContainer]: !!changeType,
        })}
        isNested={isNested}
        isLocked={isLocked}
        id={formatId(field.admin_name || "")}
      >
        {changeType && <RenewalChangeMsg changeType={changeType} />}
        {!labelHidden && <Label {...labelProps} />}
        <Field
          {...props}
          // should these be passed down? or just use it in fields that require it?
          answerContext={answerContext}
          tenant={tenant}
          field={field}
          labelId={field.key}
          onBlur={onBlur}
          required={required}
          isFocused={isFocused}
          labelProps={labelProps}
          value={value}
          onChange={onChange}
          onSave={onSave}
          onClear={onClear}
          setError={setError}
          clearError={clearError}
        />
        {!hideError && error && isString(error) && <ErrorMessage error={error} />}
      </Layout>
      {children}
    </InspectableComponent>
  );
};

function RenewalChangeMsg({ changeType }) {
  const msgType = changeType === "+" ? "added" : "updated";
  const icon = changeType === "+" ? "star" : "sync";

  return (
    <div className={styles.answerChangedMsg}>
      <Icon icon={icon} />
      <span>
        <Text t={`requirement_application.answer_${msgType}`} />
      </span>
    </div>
  );
}

FormField.propTypes = {
  field: PropTypes.shape({}).isRequired,
  hideLabel: PropTypes.bool,
  hideRequired: PropTypes.bool,
  hideError: PropTypes.bool,
  hideHelp: PropTypes.bool,
  behaviorType: PropTypes.string,
  fieldComponent: PropTypes.element,
  hideIfUnanswered: PropTypes.bool,
  horizontalLabel: PropTypes.bool,
  children: childrenPropType,
  className: PropTypes.string,
  isNested: PropTypes.bool,
};

const FormFieldFromIdent = ({ ident, ...props }) => {
  const { data: fieldEntity } = useEntity(ident);
  const { data: field, isLoading } = useFieldWithChildren(fieldEntity?.id, {
    enabled: !!fieldEntity,
  });
  if (!field || isLoading) return <Loader />;

  return <FormField field={field} {...props} />;
};

const FormFieldContainer = ({ field = null, fieldIdentifier, ...props }) => {
  if (field && field.ui_type) {
    return <FormField field={field} {...props} />;
  }
  if (field) {
    return <FormFieldFromIdent ident={field.identifier} {...props} />;
  }
  if (fieldIdentifier) {
    return <FormFieldFromIdent ident={fieldIdentifier} {...props} />;
  }
  return null;
};

export default FormFieldContainer;
